// Lead grade, in the revamp's chrome.
//
// The grid is the control: a click on a cell steps its grade, with the share
// of leads the cell holds printed under the letter so a grade is set with its
// volume in view. Beside it the grade performance is drawn in the same band
// charts as every other model page, keyed on the five grades. The two readings
// the old page kept behind an accordion stay, as tinted grids under a fold:
// they are what justifies a mapping when somebody asks.
//
// `LeadGradePage` stays mounted for a tenant without the revamp. This reads
// and writes the same store, through the same actions, so the two cannot
// disagree about what is saved.
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Button, Card } from 'react-bootstrap';

import { LeadGradeValue } from '../../../models/lead_grade/LeadGradePageModel';
import { PROFILES_PERMISSIONS } from '../../../permissions';
import Store from '../../../store';
import checkPermissions from '../../components/CheckPermissions';
import Error from '../../components/error/Error';
import Spinner from '../../components/Spinner';
import { GradeHeatmap, GradeMatrix } from '../../components/model/GradeMatrix';
import ModelPerformanceBands from '../../components/model/ModelPerformanceBands';
import './fineTuning.css';

interface LeadGradeRevampPageProps {
  store?: Store;
}

interface LeadGradeRevampPageState {
  detailsOpen: boolean;
  /** A cell changed since the matrix was last saved. Said beside Save. */
  dirty: boolean;
}

export default inject('store')(
  observer(
    class LeadGradeRevampPage extends React.Component<
      LeadGradeRevampPageProps,
      LeadGradeRevampPageState
    > {
      constructor(props: LeadGradeRevampPageProps) {
        super(props);
        this.state = { detailsOpen: false, dirty: false };
      }

      async componentDidMount() {
        const { store } = this.props;
        store.leadGradePage.loadingConf = true;
        await Promise.all([
          store.getLeadGradeConfiguration(),
          store.getLeadGradeHelpMatrixes(),
        ]);
        store.leadGradePage.loadingConf = false;
      }

      setGrade(cell: { ltb: string; fit: string }, grade: LeadGradeValue) {
        this.props.store.leadGradePage.configuration.matrix[cell.ltb][
          cell.fit
        ] = grade;
        this.setState({ dirty: true });
      }

      async save() {
        await this.props.store.calculateLeadGradePerformance({
          isProcessing: false,
        });
        this.setState({ dirty: false });
      }

      render() {
        const { leadGradePage, isAllowedToEdit } = this.props.store;
        const {
          configuration,
          performances,
          helpMatrixes,
          loadingConf,
          loadingPerformance,
          error,
        } = leadGradePage;

        if (loadingConf || !configuration || !helpMatrixes) return <Spinner />;

        return (
          <div className="ftune mt-3 mb-5">
            {error && <Error message={error} />}
            <Card className="ftune-card">
              <Card.Body>
                <div className="ftune-actions lgr-head">
                  <h5 className="ftune-title">Lead grade</h5>
                  {this.state.dirty && (
                    <span className="ftune-saving">Not saved yet</span>
                  )}
                  {checkPermissions(
                    PROFILES_PERMISSIONS.ARCHITECT,
                    <Button
                      size="sm"
                      variant="primary"
                      data-testid="lead-grade-save"
                      disabled={loadingPerformance || !isAllowedToEdit}
                      onClick={() => this.save()}
                    >
                      {loadingPerformance
                        ? 'Computing...'
                        : 'Save & compute performance'}
                    </Button>
                  )}
                </div>
                <p className="ftune-hint">
                  A lead&apos;s grade is where its fit segment meets its
                  engagement segment.
                </p>
                <GradeMatrix
                  matrix={configuration.matrix}
                  shares={helpMatrixes.sumPopulation}
                  disabled={!isAllowedToEdit}
                  onChange={(cell, grade) => this.setGrade(cell, grade)}
                />
              </Card.Body>
            </Card>

            {loadingPerformance && <Spinner />}
            {!performances && !loadingPerformance && (
              <p className="ftune-hint">
                Save &amp; compute performance to read the leads and conversions
                each grade holds.
              </p>
            )}
            {performances && (
              <ModelPerformanceBands
                performances={performances}
                vocabulary="lead_grade"
                title="Performance"
                scope="Validation dataset, current matrix"
              />
            )}

            <Card className="ftune-card">
              <Card.Body>
                <button
                  type="button"
                  className="tpf-toggle"
                  aria-expanded={this.state.detailsOpen}
                  onClick={() =>
                    this.setState({ detailsOpen: !this.state.detailsOpen })
                  }
                >
                  <span className="tpf-chevron" aria-hidden="true">
                    {this.state.detailsOpen ? '▼' : '►'}
                  </span>
                  <span className="tpf-tree">More details</span>
                  <span className="tpf-scope">
                    What each cell converts at, and how many leads it holds
                  </span>
                </button>
                {this.state.detailsOpen && (
                  <div className="lgr-details">
                    <div>
                      <h6 className="tpf-title">Conversion rate per cell</h6>
                      <GradeHeatmap
                        matrix={helpMatrixes.averageConversionRate}
                        format={(value) => `${value.toFixed(1)}%`}
                      />
                      <p className="ftune-hint lgr-note">
                        Converted leads over all leads in the cell, in the
                        validation dataset.
                      </p>
                    </div>
                    <div>
                      <h6 className="tpf-title">Share of leads per cell</h6>
                      <GradeHeatmap
                        matrix={helpMatrixes.sumPopulation}
                        format={(value) => `${value.toFixed(1)}%`}
                      />
                      <p className="ftune-hint lgr-note">
                        Leads in the cell over all leads in the validation
                        dataset.
                      </p>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>
        );
      }
    }
  )
);
